// Gestionnaire de synchronisation hors ligne : file d'attente persistée,
// resynchronisation automatique au retour du réseau, anti-doublons et détection de conflits.
import api from "@/lib/api";
import { toast } from "sonner";

const QKEY = "signa_sync_queue";
let listeners = [];
let flushing = false;
let conflictHandler = null;
let savedHandler = null;
let flushedHandler = null;

function load() {
  try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; }
}
function save(q) { localStorage.setItem(QKEY, JSON.stringify(q)); notify(q); }
function notify(q) { listeners.forEach((l) => l(q || load())); }

export function subscribe(fn) {
  listeners.push(fn);
  fn(load());
  return () => { listeners = listeners.filter((x) => x !== fn); };
}
export function pendingCount(visitId) {
  return load().filter((i) => !visitId || i.visitId === visitId).length;
}
export function isOnline() { return typeof navigator === "undefined" ? true : navigator.onLine; }

export function setConflictHandler(fn) { conflictHandler = fn; }
export function setSavedHandler(fn) { savedHandler = fn; }
export function setFlushedHandler(fn) { flushedHandler = fn; }

// Une seule mise à jour en attente par visite (la plus récente écrase la précédente) -> anti-doublon.
export function enqueueVisitUpdate(visitId, payload, baseUpdatedAt) {
  const q = load().filter((i) => !(i.type === "visit_update" && i.visitId === visitId));
  q.push({ id: crypto.randomUUID(), type: "visit_update", visitId, payload, baseUpdatedAt, createdAt: Date.now() });
  save(q);
  return flush();
}

export function enqueuePhoto(visitId, dataUrl, filename, caption, zoneId) {
  const q = load();
  q.push({ id: crypto.randomUUID(), type: "photo_upload", visitId, dataUrl, filename, caption, zoneId, createdAt: Date.now() });
  save(q);
  return flush();
}

function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = (meta.match(/:(.*?);/) || [null, "image/jpeg"])[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function flush() {
  if (flushing) return { status: "busy" };
  if (!isOnline()) return { status: "offline" };
  flushing = true;
  let touchedVisits = new Set();
  try {
    let q = load();
    while (q.length) {
      const item = q[0];
      try {
        if (item.type === "visit_update") {
          const body = { ...item.payload, base_updated_at: item.baseUpdatedAt };
          const { data } = await api.put(`/visits/${item.visitId}`, body);
          // propage le nouvel horodatage aux éventuelles mises à jour suivantes de la même visite
          q.slice(1).forEach((it) => {
            if (it.type === "visit_update" && it.visitId === item.visitId) it.baseUpdatedAt = data.updated_at;
          });
          if (savedHandler) savedHandler(item.visitId, data);
        } else if (item.type === "photo_upload") {
          const fd = new FormData();
          fd.append("file", dataUrlToBlob(item.dataUrl), item.filename || "photo.jpg");
          fd.append("caption", item.caption || "");
          if (item.zoneId) fd.append("zone_id", item.zoneId);
          await api.post(`/visits/${item.visitId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        touchedVisits.add(item.visitId);
        q.shift(); save(q);
      } catch (e) {
        const status = e.response?.status;
        if (status === 409) {
          q.shift(); save(q);
          if (conflictHandler) conflictHandler(item.visitId);
        } else if (!e.response) {
          // erreur réseau -> on garde la file pour un prochain essai
          flushing = false;
          return { status: "offline" };
        } else {
          // erreur serveur définitive (403/422...) -> on retire pour éviter une boucle
          q.shift(); save(q);
          toast.error("Modification rejetée par le serveur — non enregistrée");
        }
      }
      q = load();
    }
  } finally { flushing = false; }
  if (flushedHandler) touchedVisits.forEach((vid) => flushedHandler(vid));
  return { status: "synced" };
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => flush());
}
